import {Box} from '@mui/material';
import styles from './style.module.scss';
import AurAskLioQuery from './AurAskLioQuery';
import {
  AskLioQuery,
  AskLioRecommnededQuestion,
} from '../../types/ask-lio/askLioTypes';
import AurAskLioResponse from './AurAskLioResponse';
import {useEffect, useRef} from 'react';
import AurAskLioResponsePrompt from './AurAskLioResponsePrompt';
import AurIntelligenceLoader from '../common/aur-intelligence-loader/AurIntelligenceLoader';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  setFeedbackPromptVisibility,
  setFeedbackVisibility,
  setTriggerScrollToBottom,
} from '../../redux/slices/askLioSlice';
import AurAskLioFeedbackPrompt from './AurAskLioFeedbackPrompt';
import AurAskLioHi from './AurAskLioHi';
import AurAskLioRecommnededQuestion from './AurAskLioRecommnededQuestion';
import {t} from 'i18next';

const SCROLL_TIMEOUT = 100; // Scroll trigger timeout in milli second

type AurAskLioChatBodyProps = {
  chat: AskLioQuery[];
  testId?: string;
  triggerScroll?: boolean;
  showSuggestedQuestionBlock: boolean;
  suggestedQuestions: AskLioRecommnededQuestion[];
  feedbackToastVisible?: boolean;
  onHandleSend: (query: string) => void;
};

const AurAskLioChatBody: React.FC<AurAskLioChatBodyProps> = ({
  chat,
  testId = '',
  triggerScroll,
  showSuggestedQuestionBlock,
  suggestedQuestions,
  onHandleSend,
}) => {
  const scrollableContainerRef = useRef<HTMLElement>(null);

  const isFeedbackVisible = useSelector(
    (state: RootState) => state.askLioSlice.isFeedbackVisible
  );
  const isFeedbackPromptVisible = useSelector(
    (state: RootState) => state.askLioSlice.isFeedbackPromptVisible
  );
  const triggerAskLioScrollToBottom = useSelector(
    (state: RootState) => state.askLioSlice.triggerScrollToBottom
  );
  const dispatch = useDispatch();
  const lastChatIndex = chat.length - 1;
  const scrollToBottom = () => {
    setTimeout(() => {
      scrollableContainerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, SCROLL_TIMEOUT);
  };
  // Scroll to bottom for new chat text
  useEffect(() => {
    scrollToBottom();
  }, [chat, triggerScroll, showSuggestedQuestionBlock, suggestedQuestions]);

  useEffect(() => {
    if (triggerAskLioScrollToBottom) {
      scrollToBottom();
      dispatch(setTriggerScrollToBottom(false));
    }
  }, [triggerAskLioScrollToBottom]);

  useEffect(() => {
    if (!isFeedbackVisible) dispatch(setFeedbackPromptVisibility(true));
  }, [chat.length]);

  return (
    <Box className={`${styles.aurAskLioChatBodyScroll}`} data-testid={testId}>
      <Box
        ref={scrollableContainerRef}
        className={`${styles.aurAskLioChatBody}`}
      >
        <AurAskLioHi show={chat.length === 0} />
        {chat.map((item, index) => (
          <Box
            className={`${styles.aurFlexColumnGap}`}
            key={index}
            {...(lastChatIndex === index &&
            !(showSuggestedQuestionBlock && suggestedQuestions?.length)
              ? {ref: scrollableContainerRef}
              : {})}
          >
            <AurAskLioQuery key={index} text={item.query} />
            {item.isLoading ? (
              <>
                <AurAskLioResponse text={t('askLio.askLioLoadingDots')} />
                <AurIntelligenceLoader />
              </>
            ) : (
              <AurAskLioResponse text={item.content} />
            )}
            {lastChatIndex === index &&
              isFeedbackPromptVisible &&
              !item.isLoading && (
                <AurAskLioFeedbackPrompt
                  onClick={() => {
                    dispatch(setFeedbackVisibility(true));
                    dispatch(setFeedbackPromptVisibility(false));
                  }}
                />
              )}
            {lastChatIndex === index &&
              item?.additionalButtonInfo?.map((prompt, index) => (
                <AurAskLioResponsePrompt
                  key={index}
                  text={prompt}
                  onClick={() => {
                    onHandleSend(prompt);
                  }}
                />
              ))}
          </Box>
        ))}

        <AurAskLioRecommnededQuestion
          show={showSuggestedQuestionBlock}
          scrollableContainerRef={scrollableContainerRef}
          questions={suggestedQuestions}
          onHandleSend={onHandleSend}
        />
      </Box>
    </Box>
  );
};

export default AurAskLioChatBody;
